import { FC, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LocalizedLink from 'components/common/LocalizedLink';
import Modal from '../modal/modal';
import { Vault } from '../vault';
import Settings from '../settings/settings';
import Statistics from '../statistics/statistics';
import StatsHeader from '../statistics/statsHeader';
import { MyBonuses } from '../myBonuses';
import { Transactions } from '../transactions';
import CasinoHistory from '../casinoHistory/casinoHistory';
import SportsHistory from '../sportsHistory/sportsHistory';
import Notifications from '../notifications/notifications';
import AnimatedNumber from '../utils/AnimatedNumber';
import { useAppSelector } from 'store/hooks';
import { useIsPlayingRealGame } from 'hooks/useIsPlayingRealGame';
import {
  SettingsIcon,
  StatisticsIcon,
  LockOutlineIcon,
  UsersIcon,
  DiceIcon,
  SportsIcon,
  BellIcon,
  GiftIcon,
  CreditCardIcon,
  LogOutIcon,
} from 'components/icons';
import styles from './profileDropdown.module.scss';

interface ProfileDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout?: () => void;
}

const menuItems = [
  {
    id: 'settings',
    translationKey: 'common.settings',
    fallbackLabel: 'Settings',
    path: '/settings',
    icon: <SettingsIcon />,
  },
  {
    id: 'statistics',
    translationKey: 'common.statistics',
    fallbackLabel: 'Statistics',
    path: '/statistics',
    icon: <StatisticsIcon />,
  },
  {
    id: 'vault',
    translationKey: 'profileDropdown.vault',
    fallbackLabel: 'Vault',
    path: '/vault',
    icon: <LockOutlineIcon />,
  },
  {
    id: 'inviteFriends',
    translationKey: 'profileDropdown.inviteFriends',
    fallbackLabel: 'Invite Friends',
    path: '/invite-friends',
    icon: <UsersIcon />,
  },
  {
    id: 'casinoHistory',
    translationKey: 'profileDropdown.casinoHistory',
    fallbackLabel: 'Casino History',
    path: '/casino-history',
    icon: <DiceIcon />,
  },
  {
    id: 'sportsHistory',
    translationKey: 'profileDropdown.sportsHistory',
    fallbackLabel: 'Sportsbook History',
    path: '/sports-history',
    icon: <SportsIcon />,
  },
  {
    id: 'notifications',
    translationKey: 'common.notifications',
    fallbackLabel: 'Notifications',
    path: '/notifications',
    icon: <BellIcon />,
    count: 3,
  },
  {
    id: 'myBonuses',
    translationKey: 'profileDropdown.myBonuses',
    fallbackLabel: 'My Bonuses',
    path: '/my-bonuses',
    icon: <GiftIcon />,
  },
  {
    id: 'transactions',
    translationKey: 'common.transactions',
    fallbackLabel: 'Transactions',
    path: '/transactions',
    icon: <CreditCardIcon />,
  },
];

const ProfileDropdown: FC<ProfileDropdownProps> = ({ isOpen, onClose, onLogout }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModalId, setActiveModalId] = useState<string | null>(null);
  const [modalTitle, setModalTitle] = useState('');
  const { user } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const isPlayingRealGame = useIsPlayingRealGame();

  const handleItemClick = (item: (typeof menuItems)[0]) => {
    setActiveModalId(item.id);
    setModalTitle(t(item.translationKey, item.fallbackLabel));
    setIsModalOpen(true);
    onClose();
  };

  useEffect(() => {
    const handleScrollLock = () => {
      const isMobile = window.innerWidth <= 767;
      if (isOpen && isMobile) {
        document.body.style.overflow = 'hidden';
      } else {
        // Only re-enable if no other modals/search are open
        const otherModals = document.querySelectorAll(
          '[class*="modalOverlay"], [class*="searchBackdrop"]',
        );
        if (otherModals.length === 0) {
          document.body.style.overflow = '';
        }
      }
    };

    handleScrollLock();
    window.addEventListener('resize', handleScrollLock);

    return () => {
      const otherModals = document.querySelectorAll(
        '[class*="modalOverlay"], [class*="searchBackdrop"]',
      );
      if (otherModals.length === 0) {
        document.body.style.overflow = '';
      }
      window.removeEventListener('resize', handleScrollLock);
    };
  }, [isOpen]);

  const renderModalContent = () => {
    switch (activeModalId) {
      case 'vault':
        return <Vault />;
      case 'settings':
        return <Settings />;
      case 'statistics':
        return <Statistics />;
      case 'myBonuses':
        return <MyBonuses />;
      case 'transactions':
        return <Transactions />;
      case 'casinoHistory':
        return <CasinoHistory />;
      case 'sportsHistory':
        return <SportsHistory />;
      case 'notifications':
        return <Notifications onClose={handleCloseModal} />;
      default:
        return (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <h2>Coming soon...</h2>
            <p>This feature is currently under development.</p>
          </div>
        );
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    onClose();
  };

  return (
    <>
      <div className={`${styles.dropdownMenu} ${isOpen ? styles.open : ''}`}>
        <div className={styles.header}>
          <div className={styles.userInfo}>
            <div className={styles.username} title={user?.username || t('userMenu.guest', 'Guest')}>
              {user?.username || t('userMenu.guest', 'Guest')}
            </div>
            <div className={styles.email} title={user?.email}>
              {user?.email}
            </div>
          </div>
        </div>

        <div className={styles.mobileWithdrawArea}>
          <div className={styles.withdrawRows}>
            <div className={styles.withdrawRow}>
              <span className={styles.label}>{t('common.balance', 'Balance')}</span>
              {isPlayingRealGame ? (
                <span className={styles.playingText}>{t('userMenu.playing', 'Playing')}</span>
              ) : (
                <AnimatedNumber
                  value={Number(user?.balances?.[0]?.balance || 0)}
                  prefix="$"
                  className={styles.amount}
                />
              )}
            </div>
            <div className={`${styles.withdrawRow} ${styles.noBg}`}>
              <span className={styles.label}>
                {t('userMenu.availableToWithdraw', 'Available to withdraw')}
              </span>
              {isPlayingRealGame ? (
                <span className={styles.playingText}>{t('userMenu.playing', 'Playing')}</span>
              ) : (
                <AnimatedNumber
                  value={Number(user?.balances?.[0]?.withdrawable || 0)}
                  prefix="$"
                  className={styles.amount}
                />
              )}
            </div>
          </div>
          <div className={styles.divider}></div>
        </div>

        <div className={styles.gridContainer}>
          {menuItems.map((item, index) => {
            const count = item.id === 'notifications' ? user?.unreadNotificationCount : item.count;
            const displayCount = count && count > 99 ? '99+' : count;
            const hasBadge = !!count && count > 0;
            const labelText = t(item.translationKey, item.fallbackLabel);

            if (item.path === '/invite-friends') {
              return (
                <LocalizedLink
                  key={index}
                  to={item.path}
                  className={`${styles.menuItem} ${hasBadge ? styles.hasBadge : ''}`}
                  onClick={onClose}
                  title={labelText}
                >
                  <span className={styles.icon}>{item.icon}</span>
                  <span className={styles.label}>{labelText}</span>
                  {hasBadge && <span className={styles.badge}>{displayCount}</span>}
                </LocalizedLink>
              );
            }
            return (
              <button
                key={index}
                className={`${styles.menuItem} ${hasBadge ? styles.hasBadge : ''}`}
                onClick={() => handleItemClick(item)}
                title={labelText}
              >
                <span className={styles.icon}>{item.icon}</span>
                <span className={styles.label}>{labelText}</span>
                {hasBadge && <span className={styles.badge}>{displayCount}</span>}
              </button>
            );
          })}
        </div>

        <div className={styles.footer}>
          <button
            className={styles.footerItem}
            onClick={() => {
              if (onLogout) onLogout();
              onClose();
            }}
          >
            <span className={styles.icon}>
              <LogOutIcon />
            </span>
            <span className={styles.label}>{t('common.logout', 'Log Out')}</span>
          </button>
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={activeModalId === 'statistics' ? undefined : modalTitle}
        customHeader={activeModalId === 'statistics' ? <StatsHeader user={user} /> : undefined}
        className={styles.profileModal}
      >
        {renderModalContent()}
      </Modal>
    </>
  );
};

export default ProfileDropdown;
